// packages/renderer/src/viz/elements/point-map.ts
//
// `<viz-point-map>` — lat/lon points on raster tiles, drawn with Leaflet.
//
// Three decisions worth knowing before editing this:
//
// **It renders into the LIGHT DOM.** `createRenderRoot` returns `this`, so this
// element has no shadow root of its own. Leaflet styles its panes with a global
// stylesheet; behind a shadow boundary that CSS does not reach it. Every other
// element in this folder keeps its shadow root — this one cannot.
//
// **But having no shadow root of its own is not enough**, and that is what made
// maps unusable up to v0.0.372. The element is mounted inside `viz-panel`'s
// shadow root, and a `<style>` in `document.head` does not cross a shadow
// boundary either way round: `.leaflet-pane { position: absolute }` never
// applied, so every pane, tile and marker stacked in normal flow. The layers
// were all built correctly — the tiles and the circle markers were there in the
// DOM — they were simply piled down the page instead of being positioned. So the
// stylesheet goes into WHICHEVER ROOT the element actually lives in (see
// {@link adoptLeafletCss}), which is the document when it is in the light DOM
// and the host's shadow root when it is not.
//
// **Markers are `circleMarker`, not `marker`.** Leaflet's default marker is an
// image (`marker-icon.png`) resolved relative to the stylesheet, which is exactly
// the thing that breaks under a bundler and the reason half the Leaflet questions
// on the internet exist. A circle marker is SVG: no assets, no base-URL problem,
// and it can carry a magnitude by radius.
//
// **A tile failure is not a chart failure.** Tiles come from the network; the
// points do not. When tiles cannot load the markers still draw over a plain
// background and the element says so, because a map that renders blank offline is
// indistinguishable from a map with no data.

import { LitElement, html, nothing } from 'lit';
import type { PropertyValues } from 'lit';
import type { CircleMarker, Map as LeafletMap, TileLayer } from 'leaflet';
import { readChartTheme, type MapPoint } from './chart-data.js';
import { MIN_WORLD_ZOOM, WORLD_BOUNDS, wholeZoomShowingWorld } from './map-zoom.js';
import { markerRadiusRange, scaleMarkerRadii } from './marker-scale.js';
import { sameMapPoints, sameVizOptions } from './same-input.js';
import { isOffline } from '../../util/net.js';

export interface MapOptions {
  tileUrl?: string | undefined;
  attribution?: string | undefined;
  /** Marker radius in px when no weight is mapped. */
  radius?: number | undefined;
  /** Scale marker radius by the weight channel. */
  scaleByWeight?: boolean | undefined;
}

type Leaflet = typeof import('leaflet');
type StyleRoot = Document | ShadowRoot;

let leafletPromise: Promise<Leaflet> | null = null;
let cssTextPromise: Promise<string> | null = null;
/** Roots that already carry the stylesheet. Weak, so a closed window's root goes. */
const styledRoots = new WeakSet<StyleRoot>();

/**
 * Load Leaflet once.
 *
 * Lazily, so a user who never opens a map downloads neither the library nor its
 * stylesheet — the whole point of the dynamic import.
 */
function leaflet(): Promise<Leaflet> {
  leafletPromise ??= import('leaflet').then((mod) => (mod.default ?? mod) as Leaflet);
  return leafletPromise;
}

/**
 * Leaflet's stylesheet, as text, fetched once and shared by every root.
 *
 * `?inline` hands back the text rather than letting Vite inject it into the
 * document, which is the whole point: injecting it into the document is exactly
 * what does NOT work here.
 */
function leafletCssText(): Promise<string> {
  cssTextPromise ??= import('leaflet/dist/leaflet.css?inline').then((m) => m.default as string);
  return cssTextPromise;
}

/**
 * Put Leaflet's stylesheet into the root this element actually lives in.
 *
 * A shadow root is styled independently of the document, so a map mounted inside
 * `viz-panel`'s shadow root gets nothing from `document.head` — its panes stay
 * `position: static` and the map falls apart. Once per root, not once per map:
 * several maps in one window share a root and would otherwise each add a copy.
 *
 * `adoptedStyleSheets` where it exists (one parsed sheet shared by every root),
 * a `<style>` element otherwise.
 */
async function adoptLeafletCss(root: StyleRoot): Promise<void> {
  if (styledRoots.has(root)) return;
  styledRoots.add(root);
  const css = await leafletCssText();
  if ('adoptedStyleSheets' in root && typeof CSSStyleSheet === 'function') {
    try {
      leafletSheet ??= new CSSStyleSheet();
      if (leafletSheet.cssRules.length === 0) leafletSheet.replaceSync(css);
      root.adoptedStyleSheets = [...root.adoptedStyleSheets, leafletSheet];
      return;
    } catch {
      // Constructable stylesheets can be refused (a very old engine, or a CSP
      // that blocks them). The <style> below always works.
    }
  }
  const style = document.createElement('style');
  style.dataset['vizLeaflet'] = '';
  style.textContent = css;
  (root instanceof Document ? root.head : root).append(style);
}

let leafletSheet: CSSStyleSheet | null = null;

/** The document or shadow root an element is inside. */
function styleRootOf(el: Element): StyleRoot {
  const root = el.getRootNode();
  return root instanceof ShadowRoot || root instanceof Document ? root : document;
}

export class VizPointMap extends LitElement {
  /** Leaflet needs real global CSS and a measurable container — no shadow root. */
  protected override createRenderRoot(): HTMLElement {
    return this;
  }

  points: MapPoint[] = [];
  options: MapOptions = {};
  emptyText = 'No points to map.';

  private map: LeafletMap | null = null;
  private tiles: TileLayer | null = null;
  private markers: CircleMarker[] = [];
  private ro: ResizeObserver | null = null;
  private generation = 0;
  private tileError = false;
  /** The input the markers on screen were drawn from — see `same-input.ts`. */
  private drawnPoints: readonly MapPoint[] = [];
  private drawnOptions: MapOptions = {};

  static override get properties() {
    return {
      points: { attribute: false },
      options: { attribute: false },
      emptyText: { type: String },
      tileError: { state: true },
    };
  }

  override connectedCallback(): void {
    super.connectedCallback();
    this.style.display = 'block';
    this.style.position = 'relative';
    this.style.height = '100%';
    this.style.minHeight = '120px';
    // Leaflet measures its container on creation; a panel splitter changes that
    // size without a window resize event, so it has to be told. The zoom floor is a
    // function of that measurement, so it is recomputed here too — a pane dragged
    // to a new shape holds the whole world at a different zoom.
    this.ro = new ResizeObserver(() => {
      this.map?.invalidateSize();
      this.floorZoomToWorld();
    });
    this.ro.observe(this);
    // A DOM MOVE is a disconnect followed by a connect, and `disconnectedCallback`
    // below destroys the Leaflet map. Nothing was going to ask for it back: the
    // panel re-renders and hands over the same points, and `updated` compares
    // them BY VALUE (that is what stops a redraw mid-drag), so it correctly
    // decides there is nothing to draw — over a map that no longer exists. The
    // pane came back empty and stayed empty until a reload. So a reconnect asks
    // for the redraw itself.
    if (this.hasUpdated) void this.draw();
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this.generation++;
    this.ro?.disconnect();
    this.ro = null;
    this.teardownMap();
  }

  /**
   * Let the Leaflet instance go.
   *
   * Called from two places, and the second is the less obvious one: whenever the
   * CONTAINER it was built against is no longer the container on screen. The view
   * goes with it, so `fitted` is cleared too — the next draw has no pan or zoom of
   * the user's left to preserve.
   */
  private teardownMap(): void {
    this.map?.remove();
    this.map = null;
    this.tiles = null;
    this.markers = [];
    this.fitted = false;
  }

  override updated(changed: PropertyValues): void {
    // A redraw ends in `fitBounds`, so redrawing the SAME points throws away
    // wherever the user had panned and zoomed to. `viz-panel` rebuilds `points`
    // on every render, and resizing a column in the grid beside a docked map is a
    // render — the map kept snapping back mid-drag. Comparing the values is what
    // separates a data change from an optics one; see `same-input.ts`.
    if ((changed.has('points') || changed.has('options')) && !this.matchesDrawn()) void this.draw();
  }

  /**
   * Let the map zoom out until the whole world is in view, and no further.
   *
   * The world is square and a pane is not, so the limit comes from the pane's
   * SHORTER side; the longer side gets padding, which `maxBounds` centres. Anything
   * below that floor only shrinks a world the pane already held whole.
   *
   * Both dimensions, not just the width. Until v0.0.425 the floor stopped where the
   * world was as WIDE as the pane, which cannot show the world whole in anything but
   * a square: a short docked pane lost the poles, and a tall one the edges.
   *
   * `setMinZoom` also pulls the current zoom up when it sits below the new floor,
   * which is what makes a pane dragged TALLER correct itself — a taller pane holds a
   * bigger world, so the floor rises with it.
   */
  private floorZoomToWorld(): void {
    if (!this.map) return;
    const size = this.map.getSize();
    this.map.setMinZoom(wholeZoomShowingWorld(size.x, size.y));
  }

  /** Has this map ever been fitted to data? Set by the first draw that had any. */
  private fitted = false;

  /**
   * Should this draw move the map, or leave the view where the user put it?
   *
   * Only the FIRST draw fits. After that the view is the user's: they zoomed into
   * a city, and a filter in the grid above redraws the pane — which used to throw
   * them back out to the whole of the filtered set every time, so a map was
   * unusable next to a filter you were still adjusting.
   *
   * The one exception is a filter whose points are all off screen. Keeping the view
   * there would show an empty map, which reads as "the filter matched nothing"
   * rather than "your matches are elsewhere". So a set with nothing in view is
   * fitted, and a set with anything in view is left alone.
   */
  private shouldFit(bounds: { intersects(other: unknown): boolean }): boolean {
    if (!this.fitted || !this.map) {
      this.fitted = true;
      return true;
    }
    return !bounds.intersects(this.map.getBounds());
  }

  /** Are the markers on screen already the answer for the current input? */
  private matchesDrawn(): boolean {
    return sameMapPoints(this.drawnPoints, this.points) && sameVizOptions(this.drawnOptions, this.options);
  }

  private async draw(): Promise<void> {
    const gen = ++this.generation;
    // Nothing to plot: `render()` puts the empty notice where the container was,
    // so the map has to go with it. Leaving it alive was the whole of the "the map
    // craps out if I start filtering" bug — a filter matching nothing removed the
    // div from under a live Leaflet instance, which then held an orphan node and
    // drew every later marker into it, off screen.
    if (this.points.length === 0) {
      this.teardownMap();
      // Remembered, unlike an abandoned run below: the empty state IS drawn, and
      // the input has to be recorded or filtering back to the points we started
      // with reads as "already drawn" and nothing is rebuilt.
      this.drawnPoints = this.points;
      this.drawnOptions = this.options;
      return;
    }
    const host = this.querySelector('.map') as HTMLElement | null;
    if (!host) return;
    // The container Lit has on screen now, not the one the map remembers. They
    // differ after any render that swapped the empty notice back out, and a map
    // pointing at the wrong one is invisible rather than broken — so this is the
    // invariant rather than a guess at which renders can do it.
    if (this.map && this.map.getContainer() !== host) this.teardownMap();
    // The CSS has to be in place BEFORE the map is built: Leaflet measures the
    // container and positions its panes on creation, and an unstyled container
    // measures wrong.
    const [L] = await Promise.all([leaflet(), adoptLeafletCss(styleRootOf(this))]);
    if (gen !== this.generation || !this.isConnected) return;

    const theme = readChartTheme(this);
    if (!this.map) {
      // Leaflet's default `zoomSnap: 1` on purpose. Setting it to 0 — which is what
      // it takes for a FRACTIONAL minimum zoom to be reachable — also changes how
      // far one wheel tick moves: the snap is what rounds each tick up to a whole
      // level, and without it a trackpad flick moved about an eighth of one.
      // Zooming in felt broken, so the floor is a whole zoom instead.
      this.map = L.map(host, { attributionControl: true, maxBounds: WORLD_BOUNDS, maxBoundsViscosity: 1 });
    }
    if (!this.tiles) {
      this.tiles = L.tileLayer(this.options.tileUrl ?? '', {
        attribution: this.options.attribution ?? '',
        maxZoom: 19,
        // One world, not a row of them. Leaflet repeats tiles along the x axis by
        // default, so a pane wider than the world at that zoom drew Africa three
        // times — which reads as data repeating, not as a map. This is what makes
        // zooming out safe at ANY zoom, so the floor below is free to be a fit
        // rather than a fill.
        noWrap: true,
        // `noWrap` is not enough on its own, and this is not obvious: Leaflet skips
        // its out-of-bounds check whenever the CRS wraps longitude, which EPSG3857
        // does whatever the layer says. So the ring of tiles it keeps around the
        // viewport still held `/{z}/-1/{y}.png`, which every provider answers 404 —
        // wasted requests, and worse, a `tileerror` that put "Map tiles could not be
        // loaded" over a map whose tiles were all fine. `bounds` is the check that
        // does run: a tile that does not overlap the world is never created.
        bounds: WORLD_BOUNDS,
        // A pane shorter than one tile holds the whole world only below zoom 0, so
        // the map's floor can be negative — and a tile layer draws NOTHING below its
        // own `minZoom`, which defaults to 0. `minNativeZoom` is the other half: the
        // provider has no z-1 tiles, so Leaflet keeps asking for z0 and scales them.
        minZoom: MIN_WORLD_ZOOM,
        minNativeZoom: 0,
      });
      // One flag, not one per failed tile: a map panned offline fires this for
      // every tile in the viewport.
      this.tiles.on('tileerror', () => {
        if (!this.tileError) this.tileError = true;
      });
      this.tiles.addTo(this.map);
    } else if (this.options.tileUrl && this.tiles.options.attribution !== undefined) {
      this.tiles.setUrl(this.options.tileUrl);
    }

    for (const m of this.markers) m.remove();
    this.markers = [];

    const baseRadius = this.options.radius ?? 6;
    const color = theme.palette[0] ?? '#2563eb';
    // One pass over the whole set, because a radius depends on the OTHER points:
    // the range is normalised over the weights actually present. See
    // `marker-scale.ts` for why it is a range rather than a multiplier.
    const range = markerRadiusRange(baseRadius);
    const radii = this.options.scaleByWeight ? scaleMarkerRadii(this.points, range.min, range.max) : null;

    for (const [index, p] of this.points.entries()) {
      const marker = L.circleMarker([p.lat, p.lon], {
        radius: radii ? (radii[index] ?? range.min) : baseRadius,
        color,
        weight: 1,
        fillColor: color,
        fillOpacity: 0.6,
      });
      if (p.label) marker.bindTooltip(String(p.label));
      marker.addTo(this.map);
      this.markers.push(marker);
    }

    // Fit to the data rather than opening on a world view — a map of three
    // Swiss cities opened at zoom 0 looks like an empty map.
    const bounds = L.latLngBounds(this.points.map((p) => [p.lat, p.lon] as [number, number]));
    // The floor first: `fitBounds` clamps to it, so a set of points spread across
    // the world opens no further out than the whole world.
    this.floorZoomToWorld();
    if (bounds.isValid() && this.shouldFit(bounds)) this.map.fitBounds(bounds, { padding: [24, 24], maxZoom: 12 });
    this.map.invalidateSize();
    // Again after the size settled: the first pass measured a container Leaflet
    // had only just been given, and the floor is a function of that measurement.
    this.floorZoomToWorld();
    // Only once the markers are really on screen: a run abandoned above by the
    // generation guard has drawn nothing, and remembering its input would
    // suppress the run meant to replace it.
    this.drawnPoints = this.points;
    this.drawnOptions = this.options;
  }

  override render() {
    if (this.points.length === 0) {
      return html`<div style="display:flex;align-items:center;justify-content:center;height:100%;padding:.5rem 1rem;text-align:center;font:12px/1.5 system-ui,sans-serif;color:rgba(127,127,127,.9)">
        ${this.emptyText}
      </div>`;
    }
    return html`
      <div
        class="map"
        role="img"
        aria-label="Map of ${this.points.length} ${this.points.length === 1 ? 'point' : 'points'}"
        style="position:absolute;inset:0;background:var(--viz-map-bg, #e5e7eb)"
      ></div>
      ${this.tileError
        ? html`<div role="status" style="position:absolute;left:0;right:0;bottom:0;z-index:500;padding:3px 8px;font:11px/1.35 system-ui,sans-serif;color:#92400e;background:rgba(255,251,235,.95)">
            ${isOffline()
              ? html`You are offline — the map background needs a connection, but the points are still plotted and the data is untouched.`
              : html`Map tiles could not be loaded — the points are still plotted. Check the tile URL in Settings → Visualizations, or your connection.`}
          </div>`
        : nothing}
    `;
  }
}

/** Guarded define — see `chart-element.ts`'s `defineCharts`. */
export function definePointMap(): void {
  if (!customElements.get('viz-point-map')) customElements.define('viz-point-map', VizPointMap);
}
